import { useCallback, useContext, useEffect, useMemo, useRef } from "react"
import type { Client } from "@xmpp/client"
import { createSelector } from "@reduxjs/toolkit"
import { useFeatureFlagEnabled } from "posthog-js/react"
import AdHocCommands from "@/api/adHocCommands"
import { parseHybridJsonResponse } from "@/components/cmrReport/parseHybridJsonResponse"
import {
  GPS_DOCUMENT_LINKS_FLAG,
  isGpsDocumentLinksAccount,
} from "@/lib/gpsDocumentLinks"
import type { Chat } from "@/reducers/chatsSlice"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  MessageDisplayType,
  setGpsDocumentLinks,
  type DocumentLink,
} from "@/reducers/messagesSlice"
import type { RootState } from "@/reducers/store"
import { XmppContext } from "@/stream/xmppClient"

// GPS assigns a document about 21 s (p95 30 s) after the driver sends it.
export const REFETCH_AFTER_DOCUMENT_MS = 40_000
const RECENT_MESSAGES = 50
// Wizard's limit per request; the newest documents matter most.
const MAX_DOCUMENT_MESSAGES = 1000

type GpsLink = Omit<
  DocumentLink,
  "id" | "to" | "from" | "type" | "displayType"
> & { chatJid: string }

export const fetchGpsDocumentLinks = async (
  client: Client,
  roomJid: string,
  messageIds: string[],
): Promise<DocumentLink[]> => {
  const { links = [] } = await parseHybridJsonResponse<{ links?: GpsLink[] }>(
    await AdHocCommands.getGpsDocumentLinks(client, roomJid, messageIds),
  )
  return links.map(({ chatJid, ...link }) => ({
    ...link,
    id: `gps-${link.cmrMessageId}`,
    from: chatJid,
    to: roomJid,
    type: "groupchat",
    displayType: MessageDisplayType.DocumentLink,
  }))
}

// IDs of the file messages the chat has loaded, as one string so the selector
// result compares by value. The CMR room shows forwarded files, which keep
// the driver room's message ID, the ID GPS stores.
const makeSelectDocumentMessageIds = (chatJid: string) =>
  createSelector(
    (state: RootState) => state.messages[chatJid]?.messages,
    (messages = []) =>
      messages
        .flatMap((message) =>
          message.url
            ? [message.id]
            : message.forwarded?.url && message.forwarded.id
              ? [message.forwarded.id]
              : [],
        )
        .slice(-MAX_DOCUMENT_MESSAGES)
        .join(","),
  )

// Newest file among recent messages; the CMR room shows forwarded files.
const selectNewestFile = (chatJid: string) => (state: RootState) => {
  const messages = state.messages[chatJid]?.messages ?? []
  const stop = Math.max(0, messages.length - RECENT_MESSAGES)
  for (let i = messages.length - 1; i >= stop; i--) {
    if (messages[i].url || messages[i].forwarded?.url) return messages[i]
  }
  return undefined
}

/**
 * Keep the chat's GPS document links in sync with the GPS database: read them
 * for the loaded document messages when the chat opens or loads more, and
 * once more after a new document, when GPS has had time to match it. Wizard
 * looks them up by message ID, which is indexed.
 */
export const useGpsDocumentLinksSync = (chat: Chat) => {
  const { client, myJid } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const enabled =
    isGpsDocumentLinksAccount(
      myJid?.getDomain(),
      useFeatureFlagEnabled(GPS_DOCUMENT_LINKS_FLAG),
    ) && !!chat.room
  const selectDocumentMessageIds = useMemo(
    () => makeSelectDocumentMessageIds(chat.jid),
    [chat.jid],
  )
  const documentMessageIds = useAppSelector(selectDocumentMessageIds)
  const newestFile = useAppSelector(selectNewestFile(chat.jid))
  const latestRequest = useRef(0)

  const sync = useCallback(() => {
    if (!enabled || !client || !documentMessageIds) return
    const request = ++latestRequest.current
    fetchGpsDocumentLinks(client, chat.jid, documentMessageIds.split(","))
      .then((links) => {
        // A slower, older response must not overwrite a newer one.
        if (request !== latestRequest.current) return
        dispatch(setGpsDocumentLinks({ chatJid: chat.jid, links }))
      })
      .catch((error) =>
        console.warn("Could not load GPS document links", error),
      )
  }, [enabled, client, documentMessageIds, chat.jid, dispatch])

  useEffect(sync, [sync])

  const newestFileId = newestFile?.id
  const newestFileTime = newestFile?.timestamp
  useEffect(() => {
    if (!newestFileTime) return
    // Only a document that just arrived can still be waiting for GPS.
    const age = Date.now() - new Date(newestFileTime).getTime()
    if (age > REFETCH_AFTER_DOCUMENT_MS) return
    const timer = setTimeout(sync, REFETCH_AFTER_DOCUMENT_MS - age)
    return () => clearTimeout(timer)
  }, [newestFileId, newestFileTime, sync])
}
